import { assert, assertNever, defined, type Writable } from "@glideapps/ts-necessities";
import type { BuilderAction } from "./builder-actions";
import {
    ActionNodeKind,
    type ActionNode,
    type AutomationRootNode,
    type ConditionalActionNode,
    type ConditionalFlowNode,
    type FlowActionNode,
    type LoopNode,
    type PrimitiveActionNode,
} from "./compound-action";
import {
    type SkeletonActionNode,
    type SkeletonBuilderAction,
    type SkeletonConditionalActionNode,
    type SkeletonConditionalFlowNode,
} from "./skeleton-types";
import { deepEqual } from "@glide/support";

/**
 * Creates a map of node key to node for quick lookup
 */
export function createActionNodeMap(
    node: ActionNode,
    map: Map<string, ActionNode> = new Map()
): Map<string, ActionNode> {
    map.set(node.key, node);

    switch (node.kind) {
        case ActionNodeKind.Flow:
            node.actions.forEach(action => createActionNodeMap(action, map));
            break;
        case ActionNodeKind.ConditionalFlow:
            createActionNodeMap(node.flow, map);
            break;
        case ActionNodeKind.Conditional:
            const conditionalNode = node;
            conditionalNode.conditionalFlows.forEach(flow => createActionNodeMap(flow, map));
            if (conditionalNode.elseFlow !== undefined) {
                createActionNodeMap(conditionalNode.elseFlow, map);
            }
            break;
        case ActionNodeKind.Loop:
            createActionNodeMap(node.flow, map);
            break;
        case ActionNodeKind.AutomationRoot:
            createActionNodeMap(node.flow, map);
            break;
        case ActionNodeKind.Primitive:
            break;
        default:
            assertNever(node);
    }

    return map;
}

/**
 * Hydrates a skeleton node with data from the original action
 */
function hydrateSkeletonNode(skeletonNode: SkeletonActionNode, originalNodeMap: Map<string, ActionNode>): ActionNode {
    const originalNode = originalNodeMap.get(skeletonNode.key);

    const hydratedNode = originalNode === undefined ? skeletonNode : originalNode;

    switch (skeletonNode.kind) {
        case ActionNodeKind.Primitive:
            return hydratedNode as PrimitiveActionNode;

        case ActionNodeKind.Flow: {
            const hydratedActions = skeletonNode.actions.map(skelAction =>
                hydrateSkeletonNode(skelAction, originalNodeMap)
            );

            return {
                ...hydratedNode,
                actions: hydratedActions as readonly (PrimitiveActionNode | ConditionalActionNode | LoopNode)[],
            } as FlowActionNode;
        }

        case ActionNodeKind.ConditionalFlow: {
            const hydratedFlow = hydrateSkeletonNode(skeletonNode.flow, originalNodeMap);

            return {
                ...hydratedNode,
                flow: hydratedFlow as FlowActionNode,
            } as ConditionalFlowNode;
        }

        case ActionNodeKind.Conditional: {
            const hydratedConditionalFlows = skeletonNode.conditionalFlows
                .map(skelFlow => hydrateSkeletonNode(skelFlow, originalNodeMap))
                .filter((node): node is ConditionalFlowNode => node !== undefined);

            const hydratedElseFlow =
                skeletonNode.elseFlow !== undefined
                    ? (hydrateSkeletonNode(skeletonNode.elseFlow, originalNodeMap) as FlowActionNode)
                    : undefined;

            return {
                ...hydratedNode,
                conditionalFlows: hydratedConditionalFlows,
                elseFlow: hydratedElseFlow,
            } as ConditionalActionNode;
        }

        case ActionNodeKind.Loop: {
            const hydratedFlow = hydrateSkeletonNode(skeletonNode.flow, originalNodeMap);

            return {
                ...hydratedNode,
                flow: hydratedFlow as FlowActionNode,
            } as LoopNode;
        }

        case ActionNodeKind.AutomationRoot: {
            const hydratedFlow = hydrateSkeletonNode(skeletonNode.flow, originalNodeMap);

            return {
                ...hydratedNode,
                flow: hydratedFlow as FlowActionNode,
            } as AutomationRootNode;
        }

        default:
            assertNever(skeletonNode);
    }
}

export function mutateBuilderActionWithSkeleton(
    action: BuilderAction["action"],
    skeleton: SkeletonBuilderAction["action"]
): BuilderAction["action"] {
    const originalNodeMap = createActionNodeMap(action);
    const hydratedAction = hydrateSkeletonNode(skeleton, originalNodeMap);

    return hydratedAction as ConditionalActionNode | FlowActionNode | AutomationRootNode;
}

export function skeletonizeAction(node: ActionNode): SkeletonActionNode {
    switch (node.kind) {
        case ActionNodeKind.Primitive:
            return {
                kind: ActionNodeKind.Primitive,
                key: node.key,
            };

        case ActionNodeKind.Flow: {
            const flowNode = node as FlowActionNode;
            return {
                kind: ActionNodeKind.Flow,
                key: node.key,
                actions: flowNode.actions.map(skeletonizeAction),
            };
        }

        case ActionNodeKind.ConditionalFlow: {
            const conditionalFlowNode = node as ConditionalFlowNode;
            return {
                kind: ActionNodeKind.ConditionalFlow,
                key: node.key,
                flow: skeletonizeAction(conditionalFlowNode.flow),
            } as SkeletonActionNode;
        }

        case ActionNodeKind.Conditional: {
            const conditionalNode = node as ConditionalActionNode;
            const skeleton: SkeletonActionNode = {
                kind: ActionNodeKind.Conditional,
                key: node.key,
                conditionalFlows: conditionalNode.conditionalFlows.map(skeletonizeAction),
                ...(conditionalNode.elseFlow !== undefined && {
                    elseFlow: skeletonizeAction(conditionalNode.elseFlow),
                }),
            } as SkeletonActionNode;

            return skeleton;
        }

        case ActionNodeKind.Loop: {
            const loopNode = node as LoopNode;
            return {
                kind: ActionNodeKind.Loop,
                key: node.key,
                flow: skeletonizeAction(loopNode.flow),
            } as SkeletonActionNode;
        }

        case ActionNodeKind.AutomationRoot: {
            const automationNode = node as AutomationRootNode;
            return {
                kind: ActionNodeKind.AutomationRoot,
                key: node.key,
                flow: skeletonizeAction(automationNode.flow),
            } as SkeletonActionNode;
        }

        default:
            assertNever(node);
    }
}

/**
 * Walks a builder action tree and replaces a node with the given key
 * with a partial update (spreads the replacement over the existing node)
 */
export function replaceNodeByKey(
    action: BuilderAction["action"],
    targetKey: string,
    replacement: Partial<ActionNode> // everything but the kids ActionNodeWithoutChildren
): BuilderAction["action"] {
    // Helper function to walk and replace nodes
    function walkAndReplace(node: ActionNode): ActionNode {
        // If this is the target node, spread the replacement over it
        if (node.key === targetKey) {
            assert(node.kind === replacement.kind);
        }

        const unpackTarget = node.key === targetKey ? replacement : node;

        switch (node.kind) {
            case ActionNodeKind.Primitive:
                return unpackTarget as PrimitiveActionNode;

            case ActionNodeKind.Flow:
                return {
                    ...unpackTarget,
                    actions: node.actions.map(walkAndReplace),
                } as FlowActionNode;

            case ActionNodeKind.ConditionalFlow:
                return {
                    ...unpackTarget,
                    flow: walkAndReplace(node.flow) as FlowActionNode,
                } as ConditionalFlowNode;

            case ActionNodeKind.Conditional:
                return {
                    ...unpackTarget,
                    conditionalFlows: node.conditionalFlows.map(flow => walkAndReplace(flow) as ConditionalFlowNode),
                    ...(node.elseFlow !== undefined && {
                        elseFlow: walkAndReplace(node.elseFlow) as FlowActionNode,
                    }),
                } as ConditionalActionNode;

            case ActionNodeKind.Loop:
                return {
                    ...unpackTarget,
                    flow: walkAndReplace(node.flow) as FlowActionNode,
                } as LoopNode;

            case ActionNodeKind.AutomationRoot:
                return {
                    ...unpackTarget,
                    flow: walkAndReplace(node.flow) as FlowActionNode,
                } as AutomationRootNode;

            default:
                assertNever(node);
        }
    }

    // Return a new BuilderAction with the updated action tree
    return walkAndReplace(action) as FlowActionNode | ConditionalActionNode | AutomationRootNode;
}

function replaceSkeletonNodeByKey(
    action: SkeletonActionNode,
    targetKey: string,
    replacement: ActionNode
): SkeletonActionNode {
    // Helper function to walk and replace nodes
    function walkAndReplace(node: SkeletonActionNode): SkeletonActionNode {
        // If this is the target node, spread the replacement over it
        if (node.key === targetKey) {
            return replacement;
        }

        // Otherwise, recursively process children based on node kind
        switch (node.kind) {
            case ActionNodeKind.Primitive:
                // No children to process
                return node;

            case ActionNodeKind.Flow:
                return {
                    ...node,
                    actions: node.actions.map(walkAndReplace),
                };

            case ActionNodeKind.ConditionalFlow:
                return {
                    ...node,
                    flow: walkAndReplace(node.flow),
                } as SkeletonConditionalFlowNode;

            case ActionNodeKind.Conditional:
                return {
                    ...node,
                    conditionalFlows: node.conditionalFlows.map(flow => walkAndReplace(flow)),
                    ...(node.elseFlow !== undefined && {
                        elseFlow: walkAndReplace(node.elseFlow),
                    }),
                } as SkeletonConditionalActionNode;

            case ActionNodeKind.Loop:
                return {
                    ...node,
                    flow: walkAndReplace(node.flow),
                } as LoopNode;

            case ActionNodeKind.AutomationRoot:
                return {
                    ...node,
                    flow: walkAndReplace(node.flow),
                } as AutomationRootNode;

            default:
                assertNever(node);
        }
    }

    // Return a new BuilderAction with the updated action tree
    return walkAndReplace(action) as FlowActionNode | ConditionalActionNode | AutomationRootNode;
}

interface StrippedNode {
    readonly key: string;
    readonly kind: ActionNodeKind;
    // Plus other fields
}

export function stripActionNode(node: ActionNode): StrippedNode {
    const stripped = { ...node };
    switch (stripped.kind) {
        case ActionNodeKind.Primitive:
            return stripped;
        case ActionNodeKind.Flow:
            delete (stripped as Writable<Partial<FlowActionNode>>).actions;
            return stripped;
        case ActionNodeKind.ConditionalFlow:
            delete (stripped as Writable<Partial<ConditionalFlowNode>>).flow;
            return stripped;
        case ActionNodeKind.Conditional:
            delete (stripped as Writable<Partial<ConditionalActionNode>>).conditionalFlows;
            delete (stripped as Writable<Partial<ConditionalActionNode>>).elseFlow;
            return stripped;
        case ActionNodeKind.Loop:
            delete (stripped as Writable<Partial<LoopNode>>).flow;
            return stripped;
        case ActionNodeKind.AutomationRoot:
            delete (stripped as Writable<Partial<AutomationRootNode>>).flow;
            return stripped;
        default:
            assertNever(stripped);
    }
}

/**
 * Compares two action nodes and returns the differences as a skeleton and changed nodes list
 */
export function diffActionNodes(
    start: BuilderAction["action"],
    end: BuilderAction["action"]
): {
    readonly skeletonBefore: SkeletonActionNode;
    readonly skeleton: SkeletonActionNode | undefined;
    readonly changedNodesBefore: StrippedNode[];
    readonly changedNodes: StrippedNode[];
    readonly deletedNodes: StrippedNode[];
} {
    const startMap = createActionNodeMap(start);
    const endMap = createActionNodeMap(end);

    const isNodeDifferent = (key: string): boolean => {
        const startNode = startMap.get(key);
        const endNode = endMap.get(key);

        if (startNode === undefined || endNode === undefined) {
            return false; // deleted nodes or new nodes are in the skeleton
        }

        return !deepEqual(stripActionNode(startNode), stripActionNode(endNode), {
            strict: true,
        });
    };

    const newNodes = Array.from(endMap.keys()).filter(key => !startMap.has(key));
    const changedNodes: StrippedNode[] = [];
    const changedNodesBefore: StrippedNode[] = [];
    const deletedNodes: StrippedNode[] = [];
    for (const [key, startNode] of startMap) {
        const endNode = endMap.get(key);
        if (endNode === undefined) {
            deletedNodes.push(stripActionNode(startNode));
        } else if (isNodeDifferent(key)) {
            changedNodes.push(stripActionNode(endNode));
            changedNodesBefore.push(stripActionNode(startNode));
        }
    }

    const startSkeleton = skeletonizeAction(start);
    let endSkeleton = skeletonizeAction(end);

    const hasStructuralChanges = !deepEqual(startSkeleton, endSkeleton, {
        strict: true,
    });

    for (const key of newNodes) {
        endSkeleton = replaceSkeletonNodeByKey(endSkeleton, key, defined(endMap.get(key)));
    }

    return {
        skeletonBefore: startSkeleton,
        skeleton: hasStructuralChanges ? endSkeleton : undefined,
        changedNodesBefore,
        changedNodes,
        deletedNodes,
    };
}
